import { useCallback, useState } from 'react';

const STORAGE_KEY = 'pp.recentEmoji';

/** Fills the 3×5 grid exactly, so it never looks half-empty on a first visit. */
export const RECENT_SLOTS = 15;

/**
 * What the grid holds before anyone has thrown anything — the original set of
 * projectiles, so the picker opens on something usable rather than empty.
 */
export const DEFAULT_THROWABLES = [
  '🍅',
  '🥚',
  '🎉',
  '👏',
  '🔥',
  '💩',
  '❤️',
  '🍺',
  '☕',
  '🧀',
  '🚀',
  '🏆',
  '🍌',
  '⚡',
  '🌮',
];

const read = (): string[] => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_THROWABLES;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_THROWABLES;
    const stored = parsed.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);
    // Top up from the defaults so the grid stays full even if the stored list
    // was written by an older version with fewer slots.
    return [...new Set([...stored, ...DEFAULT_THROWABLES])].slice(0, RECENT_SLOTS);
  } catch {
    return DEFAULT_THROWABLES;
  }
};

const write = (emoji: string[]): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(emoji));
  } catch {
    /* ignore — the list still works for this session */
  }
};

/**
 * The recently-thrown list, most recent first. Throwing something moves it to
 * the front and pushes the least-used one off the end, so the grid drifts
 * towards whatever this person actually throws.
 */
export const useRecentEmoji = (): [string[], (emoji: string) => void] => {
  const [recent, setRecent] = useState<string[]>(read);

  const remember = useCallback((emoji: string) => {
    setRecent((current) => {
      const next = [emoji, ...current.filter((entry) => entry !== emoji)].slice(0, RECENT_SLOTS);
      write(next);
      return next;
    });
  }, []);

  return [recent, remember];
};
